import { createContext, useContext } from "react";

/**
 * Which sign-in affordances the current build offers.
 *
 * The admin build (ba.<domain>) is served from its own origin, and two of
 * these do not survive the move:
 *
 * - Passkeys. Cognito pins one WebAuthn relying party per user pool, and ours
 *   is `app.<domain>` (infrastructure/core/cognito.tf), so the browser refuses
 *   the ceremony anywhere else.
 * - Self-serve sign-up, which has no meaning for an internal tool.
 *
 * Google does survive it: the app client's callback urls list `ba.<domain>`
 * next to `app.<domain>`, and each entry point hands Amplify its own origin as
 * the redirect (configureAmplify.ts), so the bounce through the pool domain
 * (`auth.<domain>`) lands back where it started. The marketing chrome
 * (right-hand showcase panel, Gtm, the terms-of-service line under the Google
 * button) goes with the two above.
 */
export type AuthSurface = {
    signUp: boolean;
    google: boolean;
    passkey: boolean;
    marketing: boolean;
    /**
     * Start a management-api session after signing in (POST /auth/session),
     * which is also what turns a first sign-in into an account. The admin
     * build talks to `^/backend` only, and its users have accounts already, so
     * a stranger signing in there gets a 401 rather than a trial.
     */
    serverSession: boolean;
    /**
     * Ask, between the email step and the credential step, whether the
     * address signs in through its organization's single sign-on, and send
     * the browser there (ssoDiscovery.ts). Off for the admin console: its
     * people are super admins, whom a Saml sign-in refuses by rule
     * (SessionStarter), and it talks to `^/backend` alone.
     */
    singleSignOn: boolean;
};

export const PUBLIC_AUTH_SURFACE: AuthSurface = {
    signUp: true,
    google: true,
    passkey: true,
    marketing: true,
    serverSession: true,
    singleSignOn: true,
};

export const ADMIN_AUTH_SURFACE: AuthSurface = {
    signUp: false,
    google: true,
    passkey: false,
    marketing: false,
    serverSession: false,
    singleSignOn: false,
};

export const AuthSurfaceContext =
    createContext<AuthSurface>(PUBLIC_AUTH_SURFACE);

export const useAuthSurface = () => useContext(AuthSurfaceContext);
